export interface ParcelSaleHistoryRecord {
  id: string;
  saleDate: string | null;
  saleDateLabel: string;
  saleDateMs: number | null;
  price: number | null;
  address: string;
  parcelNumber: string;
  saleType: string;
  landClass: string;
  buildingSize: number | null;
  acreage: number | null;
  deedBook: string;
  deedPage: string;
}

export interface ParcelDeedHistoryRecord {
  id: string;
  deedDate: string | null;
  deedDateLabel: string;
  deedDateMs: number | null;
  salePrice: number | null;
  deedType: string;
  book: string;
  page: string;
  party: string;
  status: string;
  revenueStamps: number | null;
}

export interface ParcelHistory {
  sales: ParcelSaleHistoryRecord[];
  deeds: ParcelDeedHistoryRecord[];
}

const parcelHistoryCache = new Map<string, Promise<ParcelHistory | null>>();

function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function normalizeSale(value: unknown): ParcelSaleHistoryRecord | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;

  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;

  return {
    id,
    saleDate: nullableString(record.saleDate),
    saleDateLabel: typeof record.saleDateLabel === 'string' ? record.saleDateLabel : 'N/A',
    saleDateMs: nullableNumber(record.saleDateMs),
    price: nullableNumber(record.price),
    address: typeof record.address === 'string' ? record.address : 'No address provided',
    parcelNumber: typeof record.parcelNumber === 'string' ? record.parcelNumber : '',
    saleType: typeof record.saleType === 'string' ? record.saleType : 'N/A',
    landClass: typeof record.landClass === 'string' ? record.landClass : 'N/A',
    buildingSize: nullableNumber(record.buildingSize),
    acreage: nullableNumber(record.acreage),
    deedBook: typeof record.deedBook === 'string' ? record.deedBook : '',
    deedPage: typeof record.deedPage === 'string' ? record.deedPage : '',
  };
}

function normalizeDeed(value: unknown): ParcelDeedHistoryRecord | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;

  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;

  return {
    id,
    deedDate: nullableString(record.deedDate),
    deedDateLabel: typeof record.deedDateLabel === 'string' ? record.deedDateLabel : 'N/A',
    deedDateMs: nullableNumber(record.deedDateMs),
    salePrice: nullableNumber(record.salePrice),
    deedType: typeof record.deedType === 'string' ? record.deedType : 'N/A',
    book: typeof record.book === 'string' ? record.book : '',
    page: typeof record.page === 'string' ? record.page : '',
    party: typeof record.party === 'string' ? record.party : '',
    status: typeof record.status === 'string' ? record.status : '',
    revenueStamps: nullableNumber(record.revenueStamps),
  };
}

function normalizeHistory(value: unknown): ParcelHistory | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.sales) || !Array.isArray(record.deeds)) return null;

  return {
    sales: record.sales.map(normalizeSale).filter((sale): sale is ParcelSaleHistoryRecord => sale !== null),
    deeds: record.deeds.map(normalizeDeed).filter((deed): deed is ParcelDeedHistoryRecord => deed !== null),
  };
}

/**
 * Fetch same-parcel sales/deed history through the local Pages Function and
 * memoize it for the current session.
 */
export async function fetchParcelHistory(parcelNumber: string): Promise<ParcelHistory | null> {
  const cacheKey = parcelNumber.trim();
  if (!cacheKey) return null;

  let cached = parcelHistoryCache.get(cacheKey);
  if (!cached) {
    const params = new URLSearchParams({ parcelNumber: cacheKey });
    cached = fetch(`/api/parcel-history?${params.toString()}`)
      .then(async (response) => {
        if (!response.ok) return null;
        return normalizeHistory(await response.json());
      })
      .catch(() => null)
      .then((history) => {
        if (history === null) parcelHistoryCache.delete(cacheKey);
        return history;
      });
    parcelHistoryCache.set(cacheKey, cached);
  }

  return cached;
}
